/**
 * Shared knowledge taxonomy (Track A · Phase-0 P0.5). The single frozen
 * source of truth for the enums, domains, link relations, and tag
 * namespaces the net-new registers (Findings / Opportunities /
 * Recommendations) and the cross-cutting capabilities (tagging, linkage,
 * cross-workshop search, insight library) all share.
 *
 * Why a separate module: the register stores, the search projection, the
 * Workshop Log sections, the Repository read-through, the Insight Library
 * rollup, and the Analyst grounding every need the same closed sets. They
 * are declared **once** here so a status/severity/domain value can never
 * drift between the store sanitiser, the `<select>` that writes it, and the
 * read site that colours it. Both tracks import these read-only.
 *
 * Convention note: per-record TypeScript *types* live with their store
 * (e.g. `Finding` in `useFindings.ts`), mirroring the R1 stores where
 * `Decision` is exported from `useDecisions.ts`. This module owns only the
 * runtime constant arrays + the literal-union types derived from them, so
 * the sanitisers, the dropdowns, and the facets share one list.
 */

/* ── Findings ──────────────────────────────────────────────────────── */

/** How serious a finding is. Ordered low→high so the Insight Library can
 *  sort/group by it. */
export const FINDING_SEVERITIES = ["info", "low", "medium", "high"] as const;
export type FindingSeverity = (typeof FINDING_SEVERITIES)[number];
/** Sanitiser fallback for an unknown severity — the neutral middle. */
export const FINDING_SEVERITY_DEFAULT: FindingSeverity = "medium";

/** Lifecycle of a finding once captured. Aligned with Track A's frozen
 *  contract (asyed70 bec5636): open → noted → actioned. */
export const FINDING_STATUSES = ["open", "noted", "actioned"] as const;
export type FindingStatus = (typeof FINDING_STATUSES)[number];
export const FINDING_STATUS_DEFAULT: FindingStatus = "open";

/** Display labels for finding severities + statuses + domains. */
export const FINDING_SEVERITY_LABELS: Record<FindingSeverity, string> = {
  info: "Info",
  low: "Low",
  medium: "Medium",
  high: "High",
};
export const FINDING_STATUS_LABELS: Record<FindingStatus, string> = {
  open: "Open",
  noted: "Noted",
  actioned: "Actioned",
};

/** The dimension a finding sits in. Fixed list (a `<select>`, not free
 *  text) so the Insight Library can group cleanly across all 8 workshops. */
export const FINDING_DOMAINS = [
  "process",
  "people",
  "technology",
  "governance",
  "data",
  "other",
] as const;
export type FindingDomain = (typeof FINDING_DOMAINS)[number];
export const FINDING_DOMAIN_DEFAULT: FindingDomain = "other";

export const FINDING_DOMAIN_LABELS: Record<FindingDomain, string> = {
  process: "Process",
  people: "People",
  technology: "Technology",
  governance: "Governance",
  data: "Data",
  other: "Other",
};

/* ── Opportunities ─────────────────────────────────────────────────── */

/** An opportunity moves identified → exploring → pursued, or is discarded. */
export const OPPORTUNITY_STATUSES = [
  "identified",
  "exploring",
  "pursued",
  "discarded",
] as const;
export type OpportunityStatus = (typeof OPPORTUNITY_STATUSES)[number];
export const OPPORTUNITY_STATUS_DEFAULT: OpportunityStatus = "identified";

/** Suggested opportunity tags (facilitators may add free-form tags too).
 *  Offered as TagInput suggestions; stored `opportunity:<tag>`-namespaced. */
export const OPPORTUNITY_TAGS: string[] = [
  "automation",
  "cost-reduction",
  "customer-experience",
  "data-quality",
  "efficiency",
  "innovation",
  "process-improvement",
  "risk-reduction",
  "revenue-growth",
  "talent",
];

/* ── Recommendations ───────────────────────────────────────────────── */

/** A recommendation is drafted, proposed, then accepted or rejected. The
 *  `body` is the AI-draft target (filled manually now; by `AI-REC` later). */
export const RECOMMENDATION_STATUSES = [
  "draft",
  "proposed",
  "accepted",
  "rejected",
] as const;
export type RecommendationStatus = (typeof RECOMMENDATION_STATUSES)[number];
export const RECOMMENDATION_STATUS_DEFAULT: RecommendationStatus = "draft";

/* ── Linkage ───────────────────────────────────────────────────────── */

/** Relation kinds for a traceability link between two records — the
 *  Traceability view reads "<from> <relation> <to>". Aligned with Track A's
 *  frozen contract (asyed70 bec5636); `related-to` is the neutral default. */
export const LINK_RELATIONS = [
  "informs",
  "contradicts",
  "depends-on",
  "leads-to",
  "supports",
  "blocks",
  "related-to",
] as const;
export type LinkRelation = (typeof LINK_RELATIONS)[number];
export const LINK_RELATION_DEFAULT: LinkRelation = "related-to";

export const LINK_RELATION_LABELS: Record<LinkRelation, string> = {
  informs: "Informs",
  contradicts: "Contradicts",
  "depends-on": "Depends on",
  "leads-to": "Leads to",
  supports: "Supports",
  blocks: "Blocks",
  "related-to": "Related to",
};

/* ── Tag namespaces ────────────────────────────────────────────────── */

/**
 * Opportunity tags persist namespaced as `opportunity:<tag>` so the Insight
 * Library can facet them distinctly from free-form tags without a second
 * field. {@link nsTag} applies the prefix, {@link stripNs} removes it for
 * display, {@link isNsTag} tests membership. Idempotent: applying twice
 * does not double-prefix.
 */
export const TAG_NS_OPPORTUNITY = "opportunity" as const;

/** Prefix a bare tag with a namespace (`opportunity` → `opportunity:foo`).
 *  Idempotent — a tag already in the namespace is returned unchanged. */
export function nsTag(namespace: string, tag: string): string {
  const prefix = `${namespace}:`;
  return tag.startsWith(prefix) ? tag : `${prefix}${tag}`;
}

/** Whether `tag` belongs to `namespace`. */
export function isNsTag(namespace: string, tag: string): boolean {
  return tag.startsWith(`${namespace}:`);
}

/** Strip a namespace prefix for display (`opportunity:foo` → `foo`). A tag
 *  outside the namespace is returned unchanged. */
export function stripNs(namespace: string, tag: string): string {
  const prefix = `${namespace}:`;
  return tag.startsWith(prefix) ? tag.slice(prefix.length) : tag;
}
